import { useConfig } from '@marine/configs';
import { useMemo } from 'react';
import { useImageUrlTokenQuery } from '../../hooks/useImageUrlTokenQuery';
import {
  buildFileImageSources,
  buildFileImageUrls,
  buildPrivateFileUrl,
  buildPublicFileUrl,
  type FileImageOptions,
  type FileImageSources,
  type FileImageUrls,
  signPrivateFileUrl,
} from '../fileUrls';

/**
 * React entry points for the Laravel file service — see `../fileUrls` for the routes themselves and
 * for why every one of these takes a **uuid** rather than the numeric `laravel_id` the old
 * `mdcApiHost` service wanted.
 */

export type UseFileImageUrlsResult = FileImageUrls & {
  /**
   * The same URLs as `expo-image` sources, each carrying a token-free `cacheKey`. Mobile callers
   * should pass these to `<Image source={...} />` rather than a `{ uri }` built from the strings
   * above, so an hourly token refresh doesn't force every visible picture to download and decode
   * again.
   */
  sources: FileImageSources;
  /**
   * True only while a private file is waiting on its Clerk token. Public files never wait, and
   * neither does a caller with no uuid to show — a disabled query stays pending forever, so gating
   * a spinner on that would spin for good.
   */
  isLoading: boolean;
};

const EMPTY_RESULT: UseFileImageUrlsResult = { sources: {}, isLoading: false };

/**
 * Every rendition of a **public** file: `GET /files/public/{uuid}`, no credentials.
 *
 * Synchronous, because there is nothing to wait for — the URLs are string concatenation. Use it for
 * anything a signed-out visitor can see: checklist covers, public checklist pages, marketing.
 */
export const usePublicFileImageUrls = (
  fileUuid?: string | null,
  options?: FileImageOptions,
): UseFileImageUrlsResult => {
  const { laravelBeFullApiUrl } = useConfig();
  // Pulled apart into primitives so the memo below depends on the sizes rather than on the object
  // holding them — a caller writing `{ w: 300 }` inline passes a new object every render, and
  // depending on that would rebuild every URL each time and hand `sources` a fresh identity.
  const { w, h, q } = options ?? {};

  return useMemo(() => {
    if (!laravelBeFullApiUrl || !fileUuid) {
      return EMPTY_RESULT;
    }

    const urls = buildFileImageUrls(buildPublicFileUrl(laravelBeFullApiUrl, fileUuid), { w, h, q });

    return { ...urls, sources: buildFileImageSources(urls, fileUuid), isLoading: false };
  }, [laravelBeFullApiUrl, fileUuid, w, h, q]);
};

/**
 * Every rendition of a **private** file: `GET /files/{uuid}`, signed with a Clerk token.
 *
 * The only asynchronous part is that token, and it is shared app-wide by `useImageUrlTokenQuery` —
 * the URLs themselves are string concatenation, so they are derived here synchronously instead of
 * being cached per file. That matters on mobile: a per-file query put nine copies of a JWT into the
 * query cache for every image the user scrolled past, and the whole cache is serialised to MMKV on
 * a throttle, so scrolling a large folder meant re-stringifying a steadily growing blob on the JS
 * thread.
 */
export const usePrivateFileImageUrls = (
  fileUuid?: string | null,
  options?: FileImageOptions,
): UseFileImageUrlsResult => {
  const { laravelBeFullApiUrl } = useConfig();
  const { data: token, isPending } = useImageUrlTokenQuery({ enabled: !!fileUuid });
  // Primitives, for the same reason as in `usePublicFileImageUrls` above.
  const { w, h, q } = options ?? {};

  const urlsAndSources = useMemo(() => {
    if (!laravelBeFullApiUrl || !fileUuid || !token) {
      return { sources: {} as FileImageSources };
    }

    const urls = buildFileImageUrls(signPrivateFileUrl(buildPrivateFileUrl(laravelBeFullApiUrl, fileUuid), token), {
      w,
      h,
      q,
    });

    // The header is for React Native, which can authenticate an image request that way and so does
    // not have to rely on the token in the query string. A browser ignores it — `<img>` takes no
    // headers — which is why the URL carries the token too.
    return {
      ...urls,
      sources: buildFileImageSources(urls, fileUuid, { Authorization: `Bearer ${token}` }),
    };
  }, [laravelBeFullApiUrl, fileUuid, token, w, h, q]);

  return { ...urlsAndSources, isLoading: !!fileUuid && isPending };
};

/** Which route a file lives behind. Public files need no session; private ones 401 without one. */
export type FileVisibility = 'public' | 'private';

/**
 * Every rendition of a file on whichever route it belongs to.
 *
 * For the call sites that only learn a file's visibility at runtime — a gallery that mixes a boat's
 * own photos with a public checklist's cover. Where the answer is known statically, calling
 * `usePublicFileImageUrls` or `usePrivateFileImageUrls` directly says so in the code.
 *
 * Both hooks run on every render, as the rules of hooks require; the unused one does no work beyond
 * reading the shared token from cache.
 */
export const useFileImageUrls = (
  fileUuid?: string | null,
  { visibility = 'private', ...options }: FileImageOptions & { visibility?: FileVisibility } = {},
): UseFileImageUrlsResult => {
  const isPublic = visibility === 'public';
  const publicUrls = usePublicFileImageUrls(isPublic ? fileUuid : undefined, options);
  const privateUrls = usePrivateFileImageUrls(isPublic ? undefined : fileUuid, options);

  return isPublic ? publicUrls : privateUrls;
};

/**
 * One URL for a file's original bytes — no rendition — for downloads and non-image previews.
 *
 * Separate from the hooks above because a PDF or a video has no `thumbnail`, and because a download
 * wants the URL synchronously: a `window.open` after an `await` is a popup the browser blocks.
 *
 * `undefined` until a private file's token has arrived, and for a caller with no uuid.
 */
export const useFileUrl = (fileUuid?: string | null, visibility: FileVisibility = 'private'): string | undefined => {
  const { laravelBeFullApiUrl } = useConfig();
  const isPublic = visibility === 'public';
  const { data: token } = useImageUrlTokenQuery({ enabled: !isPublic && !!fileUuid });

  if (!laravelBeFullApiUrl || !fileUuid) {
    return undefined;
  }

  if (isPublic) {
    return buildPublicFileUrl(laravelBeFullApiUrl, fileUuid);
  }

  return token ? signPrivateFileUrl(buildPrivateFileUrl(laravelBeFullApiUrl, fileUuid), token) : undefined;
};
